import { useRef, useState, useEffect } from 'react'

import ArrowLeft from '../assets/svgs/ArrowLeft'
import ArrowRight from '../assets/svgs/ArrowRight'

export default function GraphicDesign({ sectionRef }) {
	const portfolioRef = useRef(null)

	const [canScrollLeft, setCanScrollLeft] = useState(false)
	const [canScrollRight, setCanScrollRight] = useState(false)

	const scroll = scrollOffset => {
		portfolioRef.current.scrollBy({ left: scrollOffset, behavior: 'smooth' })
	}

	const updateScrollButtons = () => {
		const el = portfolioRef.current
		if (!el) return

		setCanScrollLeft(el.scrollLeft > 0)
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
	}

	useEffect(() => {
		const el = portfolioRef.current
		if (!el) return

		const handleScroll = () => updateScrollButtons()

		el.addEventListener('scroll', handleScroll)

		// Update after layout
		requestAnimationFrame(() => {
			updateScrollButtons()
		})

		// Update again after images load (optional but good)
		const imgs = el.querySelectorAll('img')
		let loaded = 0
		imgs.forEach(img => {
			if (img.complete) {
				loaded++
			} else {
				img.addEventListener('load', () => {
					loaded++
					if (loaded === imgs.length) {
						updateScrollButtons()
					}
				})
			}
		})

		return () => el.removeEventListener('scroll', handleScroll)
	}, [])

	return (
		<section ref={sectionRef} className='slide flex-col py-8'>
			<div className='pb-6'>
				<h3>Graphic Design</h3>
				<p className='pb-3'>
					A collection showcasing a few examples of design work I&rsquo;ve
					created in my professional career.
				</p>
				<hr className='separator' />
			</div>
			<div ref={portfolioRef} className='portfolio-container'>
				<Project
					img={'wud-journal.jpg'}
					alt={`The WUD Journal cover, an employee newsletter showcasing a sleek and engaging visual style`}
				/>
				<Project
					img={'vo-cred.jpg'}
					alt={`VIP access credential for Florida Atlantic vs. Georgia Southern football game`}
				/>
				<Project
					img={'pf-gc-sc.jpg'}
					alt={`Product announcement for Powerflex LED strips by Moda Light, showcasing Static Color and Glowdim options in vibrant lighting`}
				/>
				<Project
					img={'fau-bf-ad.jpg'}
					alt={`Holiday sale advertisement for Florida Atlantic University Alumni Association`}
				/>
				<Project
					img={'rdm-graze.jpg'}
					alt={`Promotional graphic for Moda Light's Mini Graze LED fixture, now RDM-enabled with Glowdim and Dynamic White lighting, featuring sleek light bar design`}
				/>
				<Project
					img={'pss-truck-magnet.jpg'}
					alt={`Truck magnet design for Pro Sun Shades advertising custom shade installations and company contact information`}
				/>
				<Project
					img={'pt-certif.jpg'}
					alt={`Certificate of appreciation for Palm Tran bus operators, recognizing their dedication and service to the community`}
				/>
				<Project
					img={'pbcwud-backdrop.jpg'}
					alt={`Trade show backdrop showcasing Palm Beach County Water Utilities Department's commitment to quality and excellence`}
				/>
				<Project
					img={'fau-highlight.jpg'}
					alt={`Florida Atlantic University announces 12 students as finalists in the National Merit Scholarship competition`}
				/>
				<Project
					img={'cf-scholar.jpg'}
					alt={`Community Foundation scholarship application flyer designed to inform potential applicants about available opportunities`}
				/>
				<Project
					img={'hs-invite.jpg'}
					alt={`Digital invitation for the Horizon Society Luncheon at Florida Atlantic University, featuring event details for a virtual Zoom meeting with RSVP instructions`}
				/>
				<Project
					img={'place-of-hope.jpg'}
					alt={`Magazine ad promoting Community Foundation's economics and investment in hope, featuring vibrant graphics and inspiring text`}
				/>
			</div>
			<div className='flex space-x-6 justify-center'>
				<button
					onClick={() => scroll(-350)}
					disabled={!canScrollLeft}
					className='scroll-btn'
					aria-label='Scroll to the left'
				>
					<ArrowLeft />
				</button>
				<button
					onClick={() => scroll(350)}
					disabled={!canScrollRight}
					className='scroll-btn'
					aria-label='Scroll to the right'
				>
					<ArrowRight />
				</button>
			</div>
		</section>
	)
}

function Project({ img, alt }) {
	return (
		<div className='portfolio-item'>
			<img src={`/assets/imgs/graphic-design/${img}`} alt={alt} />
		</div>
	)
}
